import { Consultant, Franchise, GeneralUser } from '../types';
import api from '../client/axiosInstance';
import axios from 'axios';

const handleError = (error: unknown, message: string): never => {
  if (axios.isAxiosError(error)) {
    throw new Error(
      `${message}: ${error.response?.data?.message || error.message}`,
    );
  } else {
    throw new Error(`${message}: Unknown error occurred`);
  }
};

export const getConsultants = async (params?: {
  page: number;
  limit: number;
}): Promise<{
  total: number;
  totalFavorite: number;
  consultants: Consultant[];
  favoriteConsultants: Consultant[];
}> => {
  try {
    const response = await api.get('users', {
      params,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error fetching consultants');
    return {
      total: 0,
      totalFavorite: 0,
      consultants: [],
      favoriteConsultants: [],
    };
  }
};

export const getAdminConsultants = async ({
  search,
  page,
  limit,
}: {
  search?: string;
  page?: number;
  limit?: number;
}): Promise<{
  total: number;
  page: number;
  limit: number;
  consultants: Consultant[];
}> => {
  try {
    const response = await api.get('admin/users?role=consultant', {
      params: {
        search,
        page,
        limit,
      },
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error fetching consultants');
    return {
      total: 0,
      page: 0,
      limit: 0,
      consultants: [],
    };
  }
};

export const getAdminFranchisors = async ({
  search,
  page,
  limit,
}: {
  search?: string;
  page?: number;
  limit?: number;
}): Promise<{
  total: number;
  page: number;
  limit: number;
  franchises: Franchise[];
}> => {
  try {
    const response = await api.get('admin/users?role=franchisor', {
      params: {
        search,
        page,
        limit,
      },
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error fetching consultants');
    return {
      total: 0,
      page: 0,
      limit: 0,
      franchises: [],
    };
  }
};

export const getFranchisesWithoutFranchableName = async ({
  search,
  limit,
}: {
  search?: string;
  limit?: number;
}): Promise<{
  franchises: Franchise[];
}> => {
  try {
    const response = await api.get('admin/users?role=franchisor', {
      params: {
        search,
        limit,
        withoutFranchableName: true,
      },
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error fetching franchises without franchable name');
    return {
      franchises: [],
    };
  }
};

export const getFilteredConsultants = async (
  filter: string,
): Promise<{
  total: number;
  totalFavorite: number;
  consultants: Consultant[];
  favoriteConsultants: Consultant[];
}> => {
  const endpoint = `users?${filter}`;
  try {
    const response = await api.get(endpoint);
    return response.data;
  } catch (error) {
    handleError(error, 'Error fetching filtered consultants');
    return {
      total: 0,
      totalFavorite: 0,
      consultants: [],
      favoriteConsultants: [],
    };
  }
};

export const getConsultantById = async (
  id: string,
  isPreview?: boolean,
): Promise<Consultant | null> => {
  try {
    let url = `users/profile/${id}`;
    if (isPreview) url += '?isPreview=true';
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    handleError(error, 'Error');
    return null;
  }
};

export const getConsultantByIdAdmin = async (
  id: string,
): Promise<Consultant | null> => {
  try {
    const response = await api.get(`admin/users/${id}`);
    return response.data;
  } catch (error) {
    handleError(error, 'Error');
    return null;
  }
};

export const addFavoriteConsultant = async (
  id: string,
): Promise<Consultant | null> => {
  try {
    const response = await api.post(`users/favorite-consultant`, {
      consultantId: id,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error adding favorite consultant');
    return null;
  }
};

export const updateAdminProfile = async (
  firstName: string,
  lastName: string,
  email: string,
) => {
  const preparedData: Record<string, string> = {};
  if (firstName) {
    preparedData.firstName = firstName;
  }
  if (lastName) {
    preparedData.lastName = lastName;
  }
  if (email) {
    preparedData.email = email;
  }
  try {
    const response = await api.patch(`/users`, preparedData);
    return response.data;
  } catch (error) {
    handleError(error, 'Error updating profile');
    return null;
  }
};

export const updatePassword = async (
  currentPassword: string,
  newPassword: string,
  confirmPassword: string,
) => {
  try {
    const response = await api.patch(`/users/password`, {
      password: newPassword,
      confirmPassword,
      oldPassword: currentPassword,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error updating password');
    return null;
  }
};

